// Scheduled Updates page — MSP Portal
// Shows all queued and scheduled driver update commands for the organization

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../config";
import Navbar from "../components/Navbar";

const API_URL = config.API_URL;

export default function ScheduledUpdates() {
  const navigate = useNavigate();
  const token = sessionStorage.getItem("token");
  const storedUser = JSON.parse(sessionStorage.getItem("user") || "{}");
  const ORG_ID = storedUser.organization_id;

  const [commands, setCommands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (!token) { navigate("/login"); return; }
    fetchCommands();
  }, []);

  async function fetchCommands() {
    console.log("fetchCommands called, ORG_ID:", ORG_ID);
    try {
      const res = await axios.get(`${API_URL}/organizations/${ORG_ID}/commands`, {
        ...authHeaders,
        headers: {
          ...authHeaders.headers,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      setCommands([...res.data]);
      console.log("Commands set:", res.data.length, "items");
    } catch (err) {
      console.error("Failed to load commands", err);
    } finally {
      setLoading(false);
    }
  }

  async function handleCancel(commandId) {
    if (!window.confirm("Cancel this scheduled update?")) return;
    try {
      await axios.delete(`${API_URL}/commands/${commandId}`, authHeaders);
      fetchCommands();
    } catch (err) {
      alert("Failed to cancel command.");
    }
  }

  function statusBadge(status) {
    if (status === "completed") return "bg-green-900 text-green-300";
    if (status === "failed") return "bg-red-900 text-red-300";
    if (status === "in_progress") return "bg-blue-900 text-blue-300";
    return "bg-yellow-900 text-yellow-300";
  }

  function statusLabel(status) {
    if (status === "completed") return "Completed";
    if (status === "failed") return "Failed";
    if (status === "in_progress") return "In Progress";
    return "Pending";
  }

  function manufacturerBadge(manufacturer) {
    if (manufacturer === "hp") return "bg-blue-900 text-blue-300";
    if (manufacturer === "dell") return "bg-green-900 text-green-300";
    if (manufacturer === "lenovo") return "bg-red-900 text-red-300";
    return "bg-gray-700 text-gray-400";
  }

  const filtered = filter === "all" ? commands : commands.filter(c => c.status === filter);

  const counts = {
    all: commands.length,
    pending: commands.filter(c => c.status === "pending").length,
    in_progress: commands.filter(c => c.status === "in_progress").length,
    completed: commands.filter(c => c.status === "completed").length,
    failed: commands.filter(c => c.status === "failed").length,
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <Navbar />

      <div className="max-w-6xl mx-auto px-6 py-10">
        <h1 className="text-2xl font-bold text-white mb-1">Scheduled Updates</h1>
        <p className="text-gray-400 text-sm mb-8">View and manage all queued and scheduled driver update commands across your devices.</p>

        {/* Filter tabs */}
        <div className="flex gap-2 mb-6 flex-wrap items-center">
          {[
            { key: "all", label: "All" },
            { key: "pending", label: "Pending" },
            { key: "in_progress", label: "In Progress" },
            { key: "completed", label: "Completed" },
            { key: "failed", label: "Failed" },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                filter === f.key
                  ? "bg-blue-600 text-white"
                  : "bg-gray-800 text-gray-400 hover:text-white"
              }`}
            >
              {f.label} <span className="ml-1 opacity-70">({counts[f.key]})</span>
            </button>
          ))}

          {/* Refresh button */}
          <button
            onClick={fetchCommands}
            className="ml-auto px-4 py-1.5 rounded-lg text-sm bg-gray-800 text-gray-400 hover:text-white transition-colors"
          >
            ↻ Refresh
          </button>
        </div>

        {/* Commands list */}
        {loading ? (
          <p className="text-gray-400 text-sm">Loading...</p>
        ) : filtered.length === 0 ? (
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-8 text-center text-gray-500 text-sm">
            No update commands found.
          </div>
        ) : (
          <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-400 border-b border-gray-800">
                  <th className="text-left px-6 py-3">Device</th>
                  <th className="text-left px-6 py-3">Manufacturer</th>
                  <th className="text-left px-6 py-3">Tool</th>
                  <th className="text-left px-6 py-3">Status</th>
                  <th className="text-left px-6 py-3">Scheduled For</th>
                  <th className="text-left px-6 py-3">Completed</th>
                  <th className="text-left px-6 py-3">Result</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(cmd => (
                  <tr key={cmd.id} className="border-b border-gray-800 hover:bg-gray-800 transition-colors">
                    <td className="px-6 py-3">
                      <p className="text-white font-medium">{cmd.hostname}</p>
                      <p className="text-gray-500 text-xs">{cmd.manufacturer_model}</p>
                    </td>
                    <td className="px-6 py-3">
                      <span className={`text-xs px-2 py-1 rounded-full font-medium uppercase ${manufacturerBadge(cmd.manufacturer)}`}>
                        {cmd.manufacturer}
                      </span>
                    </td>
                    <td className="px-6 py-3">
                      <p className="text-gray-300 text-xs">{cmd.tool_name}</p>
                      <p className="text-gray-500 text-xs">v{cmd.tool_version}</p>
                    </td>
                    <td className="px-6 py-3">
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${statusBadge(cmd.status)}`}>
                        {statusLabel(cmd.status)}
                      </span>
                      {cmd.error_message && (
                        <p className="text-red-400 text-xs mt-1">{cmd.error_message}</p>
                      )}
                    </td>
                    <td className="px-6 py-3 text-gray-400 text-xs">
                      {new Date(cmd.scheduled_at).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </td>
                    <td className="px-6 py-3 text-gray-400 text-xs">
                      {cmd.completed_at
                        ? new Date(cmd.completed_at).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" })
                        : "—"}
                    </td>
                    <td className="px-6 py-3">
                      {cmd.result_summary && (
                        <p className="text-gray-300 text-xs mb-1">{cmd.result_summary}</p>
                      )}
                      {cmd.error_message && !cmd.result_summary && (
                        <p className="text-red-400 text-xs mb-1">{cmd.error_message}</p>
                      )}
                      {cmd.status === "pending" && (
                        <button
                          onClick={() => handleCancel(cmd.id)}
                          className="text-red-400 hover:text-red-300 text-xs border border-red-800 hover:border-red-600 px-2 py-1 rounded transition-colors"
                        >
                          Cancel
                        </button>
                      )}
                      {cmd.status !== "pending" && !cmd.result_summary && !cmd.error_message && (
                        <span className="text-gray-600 text-xs">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}